import {
  initialState as jsonFormsInitialState,
  preScreenInitialState,
} from "src/jsonForms/jsonFormsSlice";
import type { JsonFormsState } from "src/jsonForms/types";
import type { PreScreenState } from "src/jsonForms/preScreen/types";

/**
 * Keys on `jsonForms.preScreen` that survive a page reload via
 * `redux-remember`. Per the pre-screening design (Persistence subset
 * table) only this small subset is persisted; everything else (notably
 * `fetchedSchema`, `schemaFetchStatus`, `schemaFetchErrorMessage`, plus
 * `validationErrors`, `submission`) is reset to slice defaults so the
 * shell starts in a clean state on reload. `formData` is also persisted
 * alongside the preScreen subset.
 */
export const PERSISTED_PRE_SCREEN_KEYS = [
  "wc",
  "cargo",
  "ipl",
  "atoz",
  "resolveSchemaInput",
  "resolvedSchemaName",
  "resolvedSchemaId",
] as const satisfies ReadonlyArray<keyof PreScreenState>;

type PersistedPreScreenKey = (typeof PERSISTED_PRE_SCREEN_KEYS)[number];

/** Subset of `PreScreenState` actually written to localStorage. */
export type PersistedPreScreenState = Pick<PreScreenState, PersistedPreScreenKey>;

/**
 * Shape of the `jsonForms` slice as it appears in the localStorage
 * snapshot — the persisted preScreen subset plus formData.
 */
type PersistedJsonFormsSnapshot = {
  preScreen: PersistedPreScreenState;
  formData: { [key: string]: Record<string, unknown> };
};

const pickPersistedPreScreen = (preScreen: PreScreenState): PersistedPreScreenState =>
  PERSISTED_PRE_SCREEN_KEYS.reduce((acc, key) => {
    // The cast is sound because the literal-key array is constrained
    // to `keyof PreScreenState` via the `satisfies` clause above.
    (acc as Record<string, unknown>)[key] = preScreen[key];
    return acc;
  }, {} as PersistedPreScreenState);

/**
 * Build the persisted snapshot of the `jsonForms` slice. Drops
 * non-persisted keys (`validationErrors`, `submission`) and the
 * ephemeral preScreen keys; only the persisted preScreen subset and
 * `formData` survive.
 *
 * Exported for unit testing the persistence shape.
 */
export function buildPersistedJsonFormsState(
  jsonForms: JsonFormsState | undefined
): PersistedJsonFormsSnapshot {
  return {
    preScreen: pickPersistedPreScreen(jsonForms?.preScreen ?? preScreenInitialState),
    formData: jsonForms?.formData ?? {},
  };
}

/**
 * Rebuild the in-memory `jsonForms` slice from a persisted snapshot.
 * Non-persisted preScreen keys reset to their initial values; the
 * non-preScreen `jsonForms` keys fall back to slice defaults, except
 * `formData` which is restored from the persisted snapshot.
 */
export function rehydrateJsonFormsState(
  persisted:
    | {
        preScreen?: Partial<PersistedPreScreenState> | null;
        formData?: { [key: string]: Record<string, unknown> } | null;
      }
    | null
    | undefined
): JsonFormsState {
  const persistedPreScreen = persisted?.preScreen ?? {};
  const restored = PERSISTED_PRE_SCREEN_KEYS.reduce((acc, key) => {
    const value = persistedPreScreen[key];
    if (value !== undefined && value !== null) {
      (acc as Record<string, unknown>)[key] = value;
    }
    return acc;
  }, {} as Partial<PersistedPreScreenState>);

  return {
    formData: persisted?.formData ?? jsonFormsInitialState.formData,
    validationErrors: jsonFormsInitialState.validationErrors,
    submission: jsonFormsInitialState.submission,
    preScreen: { ...preScreenInitialState, ...restored },
  };
}

/**
 * `serialize` function passed to `rememberEnhancer`.
 *
 * With `persistWholeStore: true` the input is the picked rootState
 * containing only `rememberedKeys`. Non-`jsonForms` slices pass
 * through unchanged; the `jsonForms` slice is reduced to its
 * persistence subset before being stringified.
 */
export function preScreenPersistenceSerialize(data: unknown): string {
  if (data && typeof data === "object" && !Array.isArray(data)) {
    const root = data as Record<string, unknown>;
    if ("jsonForms" in root) {
      return JSON.stringify({
        ...root,
        jsonForms: buildPersistedJsonFormsState(root.jsonForms as JsonFormsState | undefined),
      });
    }
  }
  return JSON.stringify(data);
}

/**
 * `unserialize` function passed to `rememberEnhancer`.
 *
 * Parses the JSON blob from localStorage and rebuilds the `jsonForms`
 * slice so non-persisted keys reset to their initial values. Other
 * slices pass through unchanged.
 */
export function preScreenPersistenceUnserialize(data: string): unknown {
  const parsed = JSON.parse(data);
  if (parsed && typeof parsed === "object" && !Array.isArray(parsed) && "jsonForms" in parsed) {
    const root = parsed as Record<string, unknown>;
    return {
      ...root,
      jsonForms: rehydrateJsonFormsState(
        root.jsonForms as
          | {
              preScreen?: Partial<PersistedPreScreenState> | null;
              formData?: { [key: string]: Record<string, unknown> } | null;
            }
          | null
          | undefined
      ),
    };
  }
  return parsed;
}
